import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage, Field, Formik } from 'formik';
import * as yup from 'yup';
import Select from 'react-select';
import { Row, Container } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { showLoader, hideLoader } from '../../store/common/slice';
import { verifyLoggedIn } from '../../store/auth/actions';
import { fetchBuildingData, fetchManagerBuildingData } from '../../store/buildings/actions';
import BuildingTitle from "../../common/buildingTitle";
import { constants } from '../../common/constants/constants';
import CacheData from '../../common/utils/cacheData';
import Input from '../../common/simple/input';
import { newSelectWithBorder } from '../../common/shared/select.styled';
import useBodyClass from '../../common/hooks/useBodyClassName';
import { StyledTenantDetails } from './TenantDetails.styled';
import {BllinkLogger} from "../../common/utils/bllink_loggers";
import StringUtils from "../../common/utils/strings";
import * as Sentry from "@sentry/react";
import { translatesLegalInfoPages } from "../../common/constants/translatedLegalInfoLinks";

const TenantDetails = ({ match }) => {
    const { t , i18n} = useTranslation();
    const dispatch = useDispatch();
    const { buildingID } = match.params;
    const history = useHistory();
    const accessToken = useSelector((state) => state.auth.accessToken);

    document.title = t('titles.tenant_details');

    useBodyClass(constants.backgroundOptions.tenantDetails);

    const [buildingInfo, setBuildingInfo] = useState({
        address: '',
        apartmentNumbers: [],
        tenantDetails: ''
    });
    const [initialValues, setInitialValues] = useState({});
    const [apartmentDropdownOptions, setApartmentDropdownOptions] = useState([]);
    const isViewedAsManager = match.path.includes(constants.pages.tenantDetailsManagerUrl);

    const ownershipDropdownOptions = [
        {
            value: constants.APIContract.tenantTypes.renter,
            label: t('payments.renter')
        },
        {
            value: constants.APIContract.tenantTypes.owner,
            label: t('payments.owner')
        }
    ];

    const prefillForm = async (viewedAsManager, buildingID) => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const aptNum = urlParams.get('apt');
        const apt = apartmentDropdownOptions.find(option => option.value == aptNum);

        let initialValues = {
            apartmentNumber: apt || '',
            tenant_type: '',
            name: '',
            phone: '',
            email: '',
            comment: '',
            agrees_to_ads: '',
            terms_of_service_confirm: true,
        };
        if (viewedAsManager) {
            const managerTenantDetails = CacheData.fetchCache(
              'managerTenantDetails',
              {buildingID, typeOfCache: sessionStorage},
            );
            const selectedApartmentNumber = managerTenantDetails?.apartmentNumber;
            if (managerTenantDetails != null) {
                const apartmentNumber = apartmentDropdownOptions.find(option => option.value === managerTenantDetails?.apartmentNumber);
                const tenant_type = ownershipDropdownOptions.find(option => option.value === managerTenantDetails?.tenant_type);
                initialValues = { ...initialValues, ...managerTenantDetails, apartmentNumber, tenant_type };
            }
            if (selectedApartmentNumber && managerTenantDetails[selectedApartmentNumber]) {
                const apartmentDetails = managerTenantDetails[selectedApartmentNumber];
                const apartmentNumber = apartmentDropdownOptions.find(option => option.value === apartmentDetails?.apartmentNumber);
                const tenant_type = ownershipDropdownOptions.find(option => option.value === apartmentDetails?.tenant_type);
                initialValues = { ...initialValues, ...apartmentDetails, apartmentNumber, tenant_type };
            }
        } else {
            const tenantDetails = CacheData.fetchCache('tenantDetails');
            const apartmentNumber = apartmentDropdownOptions.find(option => option.value === tenantDetails?.apartmentNumber);
            const tenant_type = ownershipDropdownOptions.find(option => option.value === tenantDetails?.tenant_type);
            initialValues = { ...initialValues, ...tenantDetails, apartmentNumber, tenant_type };
        }
        setInitialValues(initialValues);
    };

    const fetchData = async (buildingID, viewedAs='tenant') => {
        dispatch(showLoader());
        const viewedAsManager = viewedAs === 'manager';
        let tenantDetails = {};
        const buildingData = await dispatch(fetchBuildingData({ buildingID: buildingID }));
        if (viewedAsManager){
            const managerData = await dispatch(fetchManagerBuildingData({
                buildingID: buildingID,
                options: {urlParams: { skipDisabledCheck: true, skipStats: true }}
            }));
            if (managerData) {
                tenantDetails = managerData.payload?.building?.tenants;
                if (tenantDetails == null || Object.keys(tenantDetails).length < 1) {
                    console.log('tenant details', tenantDetails);
                    Sentry.captureMessage('No tenant details available', 'error');
                }
            }
        }

        if (buildingData) {
            const { payload } = buildingData;
            setBuildingInfo({
                address: payload.address,
                apartmentNumbers: payload.allNumbers,
                tenantDetails: tenantDetails
            });
        }
        dispatch(hideLoader());
    };

    useEffect(() => {
        (async function () {
            if (isViewedAsManager) await prefillForm(true, buildingID);
            else await prefillForm(false, buildingID);
        })();
    }, [apartmentDropdownOptions]);

    useEffect(() => {
        (async function () {
            if (isViewedAsManager){
                const viewedAs = 'manager';
                if (!accessToken) await dispatch(verifyLoggedIn());
                await fetchData(buildingID, viewedAs);
            } else {
                await fetchData(buildingID);
            }
        })();
    }, []);

    useEffect(() => {
        let options = [];
        buildingInfo?.apartmentNumbers?.map(num => (
            options.push({
                value: num,
                label: num
            })
        ));
        setApartmentDropdownOptions(options)
    }, [buildingInfo.apartmentNumbers]);

    const formSchema = yup.object().shape({
        apartmentNumber: yup.object()
          .required(t('titles.forms.mandatory_field')).nullable(),
        tenant_type: yup.object()
          .required(t('titles.forms.mandatory_field')).nullable(),
        name: yup.string()
          .required(t('titles.forms.mandatory_field_full_name'))
          .min(2, t('titles.forms.min_length', {size: 2})),
        phone: yup.string()
          .required(t('titles.forms.mandatory_field'))
          .matches(/^[0-9]+$/, t('titles.forms.correct_phone'))
          .min(9, t('titles.forms.min_length', {size: 9}))
          .max(10, t('titles.forms.max_length', {size: 10})),
        email: yup.string()
            // change to regex since the FE and BE (sequlize isEmail) definition of an email are different
          .matches(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/, t('titles.forms.correct_email'))
          // .email(t('titles.forms.correct_email'))
          .optional(),
        comment: yup.string()
          .max(100, t('titles.forms.max_length_exceeded', 100))
          .optional(),
        terms_of_service_confirm: yup.boolean()
          .oneOf([true], t('titles.forms.subscribe_check'))
          .required(t('titles.forms.subscribe_check')),
        agrees_to_ads: yup.boolean(),
    });

    const saveEmailToSession = userPayload => {
        let userID = userPayload.email || userPayload.phone;
        if (window.smartlook) {
            window.smartlook('identify', userID);
        }
        if (window.userpilot) {
            window.userpilot.identify(userID, {email: userPayload.email, isTenant: true, locale_code: i18n.language});
        }
    };

    const saveDetailsAndGoToNextPage = async data => {
        const apartmentNumber = data.apartmentNumber.value;
        const tenantDetails = {...data, apartmentNumber: data.apartmentNumber.value, tenant_type: data.tenant_type.value, buildingID};
        const userPayload = {
            phone: data.phone,
            email: data.email,
        };

        if (!isViewedAsManager) saveEmailToSession(userPayload);

        if (!tenantDetails.email) delete tenantDetails.email;
        if (!tenantDetails.agrees_to_ads) delete tenantDetails.agrees_to_ads;
        CacheData.cache('tenantDetails', tenantDetails);
        let nextPageUrl;
        if (isViewedAsManager) {
            CacheData.cache('managerTenantDetails', tenantDetails, {buildingID: buildingID, typeOfCache: sessionStorage});
            // to also support getting buildingID /manager-fill-details/1904?apt=1
            // https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=62dd3b4b3257defaf50c83b7&selectedIssue=SSB-843
            const index = buildingID.indexOf('?');
            const noSearch = index === -1;
            const newBuildingID = noSearch ? buildingID : buildingID.slice(0, index);
            nextPageUrl = `/${constants.pages.paymentSelectionTabForManager}/${newBuildingID}/${apartmentNumber}`
        } else {
            nextPageUrl = `/${constants.pages.paymentSelectionUrl}/${buildingID}/${apartmentNumber}`
        }
        history.push(nextPageUrl);
    };

    const handleFormSubmit = async (values, helpers) => {
        // https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=62dd3b4b3257defaf50c83b7&selectedIssue=SSB-90
        // preventing sending email and phone with empty spaces in the beginning and in the end to backend
        // https://sentry.io/organizations/bllink/issues/3764974514/?alert_rule_id=1438500&alert_timestamp=1672298701601&alert_type=email&environment=production&project=5402476&referrer=alert_email
        // ‪mosheyacov108@gmail.com (with special char) to mosheyacov108@gmail.com (withOUT special char)

        const emailWithoutSpecialChars = StringUtils.sanitizeString(values.email);
        const phoneWithoutSpecialChars = StringUtils.sanitizeString(values.phone);
        const parsedValues = {
            ...values,
            email: emailWithoutSpecialChars,
            phone: phoneWithoutSpecialChars
        }
        await saveDetailsAndGoToNextPage(parsedValues);
        helpers.reset();
    };

    const handleApartmentChange = (e, setFieldValue) => {
        const selectedApartment = e.value;
        if (isViewedAsManager) {
            setFieldValue('apartmentNumber', e);
            const details = buildingInfo.tenantDetails?.[selectedApartment]?.tenantListData;
            let renterName = (details?.RenterName || '').replace(' ', '');
            let ownerName = (details?.OwnerName || '').replace(' ', '');
            const hasOwner =  ownerName;
            const hasRenter = renterName;
            const ownership = hasRenter ? 'renter' : (hasOwner ? 'owner' : '');
            if (ownership === 'renter') {
                setFieldValue('tenant_type', {
                    value: constants.APIContract.tenantTypes.renter,
                    label: t('payments.renter')
                })
            } else if (ownership === 'owner') {
                setFieldValue('tenant_type', {
                    value: constants.APIContract.tenantTypes.owner,
                    label: t('payments.owner')
                })
            } else setFieldValue('tenant_type', ownership)
            updateFields(ownership, selectedApartment, setFieldValue);
        } else {
            setFieldValue('apartmentNumber', e);
        }
    };

    const handleOwnershipChange = (e, values, setFieldValue) => {
        setFieldValue('tenant_type', e);
        const ownership = e.value;
        const selectedApartment = values.apartmentNumber;
        updateFields(ownership, selectedApartment?.value, setFieldValue);
    };

    const updateFields = (ownership, apartmentNumber, setFieldValue) => {
        const details = buildingInfo.tenantDetails?.[apartmentNumber]?.tenantListData;
        if (details != null) {
            setFieldValue('comment', '');
            switch (ownership) {
                case 'renter':
                    setFieldValue('name', details.RenterName ?? '');
                    setFieldValue('phone', details.RenterPhone ?? '');
                    setFieldValue('email', details.RenterEmail ?? '');
                    break;
                case 'owner':
                    setFieldValue('name', details.OwnerName ?? '');
                    setFieldValue('phone', details.OwnerPhone ?? '');
                    setFieldValue('email', details.OwnerEmail ?? '');
                    break;
                default:
                    setFieldValue('name', '');
                    setFieldValue('phone', '');
                    setFieldValue('email', '');
                    break;
            }
        }
    };

    return (
        <StyledTenantDetails className='TenantDetails' id="payment-heading">
            <div className='container'>
                <BuildingTitle address={buildingInfo.address} />
                <p className='text-center'>{t('payments.fill_in_details_secondary_title')}</p>
                <div className='payments-wrapper'>
                    <h4>{t('payments.details_of_payer')}</h4>
                    <Container className="tenant-container">
                    <Formik
                            initialValues={initialValues}
                            validationSchema={formSchema}
                            enableReinitialize
                            onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}
                        >
                            {({
                                values,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                setFieldValue,
                                setFieldTouched
                            }) =>
                            {
                            return (
                                <form className="form-main-block" name="registration" onSubmit={handleSubmit}>
                                    <Row>
                                        <div className='input-item col-md-3 col-12'>
                                            <label className='label' htmlFor="apartmentNumber">* {t('payments.number_of_apartment')}:</label>
                                            <Select
                                                styles={newSelectWithBorder}
                                                options={apartmentDropdownOptions}
                                                name='apartmentNumber'
                                                onChange={e => handleApartmentChange(e, setFieldValue)}
                                                onBlur={() => setFieldTouched('apartmentNumber', true)}
                                                value={values.apartmentNumber}
                                                placeholder={t('payments.select_placeholder')}
                                            />
                                            <ErrorMessage name="apartmentNumber">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                        <div className='input-item col-md-3 col-12'>
                                            <label className='label' htmlFor="ownership">* {t('payments.type_of_tenant')}:</label>
                                            <Select
                                                styles={newSelectWithBorder}
                                                options={ownershipDropdownOptions}
                                                name='tenant_type'
                                                onChange={e => handleOwnershipChange(e, values, setFieldValue)}
                                                onBlur={() => setFieldTouched('tenant_type', true)}
                                                value={values.tenant_type}
                                                placeholder={t('payments.select_placeholder')}
                                            />
                                            <ErrorMessage name="tenant_type">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Row>

                                    <Row>
                                        <div className="input-item col-md-6 col-12">
                                            <Input
                                                name='name'
                                                label={`* ${t('payments.full_name')}:`}
                                                autocomplete='name'
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="name">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                        <div className="input-item col-md-3 col-12">
                                            <Input
                                                name='phone'
                                                label={`* ${t('payments.phone')}:`}
                                                autocomplete='tel'
                                                value={values.phone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="phone">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                        <div className="input-item col-md-3 col-12">
                                            <Input
                                                name='email'
                                                label={`${t('payments.email')}:`}
                                                autocomplete='email'
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="email">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Row>

                                    <Row>
                                        <div className="input-item col-md-12 col-12">
                                            <Input
                                                name='comment'
                                                label={t('payments.comment')}
                                                value={values.comment}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <ErrorMessage name="comment">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Row>
                                    <Row className="form-check-button">
                                        <div className="form-check col-sm-12 col-md-6 col-lg-6 col-xs-12 text-right">
                                            <label>
                                                <Field type="checkbox" id="agrees_to_ads" name="agrees_to_ads"/>
                                                <span>{t('payments.agree_to_ads')}</span>
                                            </label>

                                            <label>
                                                <Field type="checkbox" id="tos-confirm" name="terms_of_service_confirm"/>
                                                <span>
                                                    {t('payments.accept_website_tos')}&nbsp;
                                                    <a rel="noopener noreferrer" target="_blank" id="tos-link" href={translatesLegalInfoPages.termsOfUseUrl.default}>
                                                        <span>{t('payments.tos')}</span>
                                                    </a>
                                                </span>
                                            </label>
                                            <ErrorMessage name="terms_of_service_confirm">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                        <div className="form-button col-sm-12 col-md-6 col-lg-6 col-xs-12 flex-container">
                                            <input
                                                id="next-tenant-details"
                                                type="submit"
                                                name="payment_btn"
                                                value={t('payments.next')}
                                            />
                                        </div>
                                    </Row>
                                </form>
                            )}}
                        </Formik>
                    </Container>
                </div>
            </div>
        </StyledTenantDetails>
    );
};

export default TenantDetails;
